import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Checkbox,
  MenuItem,
} from "@mui/material";
import {
  SaveOrEditOrders,
} from "../../Service/AxiosService";
import { Autocomplete } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddOrders = (props: {
  open: boolean;
  onClose: () => void;
  crmTags: { id: string; name: string }[];
  tagList: { id: string; name: string }[];
  customFieldList?: { id: string; name: string }[];
  initialvalues?: any;
}): React.ReactElement => {
  const [form, setForm] = useState({
    cerboTags: [] as string[],
    crmTag: "",
    OrderName: "",
    customField: "",
    customFieldValue: "",
    selectedTag: "",
    tagNotes: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // Validation: all fields required
  const isStep1Valid = form.cerboTags.length > 0;
  const isStep2Valid = form.crmTag.trim() !== "";
  const isStep3Valid = form.OrderName.trim() !== "";

  const isFormValid = isStep1Valid && isStep2Valid && isStep3Valid;

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!isStep1Valid) errors.cerboTags = "Cerbo Tags are required";
    if (!isStep2Valid) errors.crmTag = "HL Tag is required";
    if (!isStep3Valid) errors.OrderName = "Order Name is required";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (props.initialvalues) {
      setForm({
        cerboTags: props.tagList?.filter(tag => props.initialvalues.cerboTags.includes(tag.name))?.map(tag => tag.id) || [],
        crmTag:  props.crmTags?.find?.(tag => tag.name === props.initialvalues.GHLTagName)?.id || "",
        OrderName: props.initialvalues.Ordername || "",
        customField: props.initialvalues.CustomFieldId || "",
        customFieldValue: props.initialvalues.CustomFieldValue || "",
        selectedTag: props.initialvalues.SelectedTagId || "",
        tagNotes: props.initialvalues.TagNotes || "",
      });
    } else {
      setForm({
        cerboTags: [],
        crmTag: "",
        OrderName: "",
        customField: "",
        customFieldValue: "",
        selectedTag: "",
        tagNotes: "",
      });
    }
  }, [props.initialvalues, props.open]);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    setForm({
      cerboTags: [],
      crmTag: "",
      OrderName: "",
      customField: "",
      customFieldValue: "",
      selectedTag: "",
      tagNotes: "",
    });
    setError(null);
    setValidationErrors({});
    props.onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setError("Please fill in all required fields.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await SaveOrEditOrders({
        id: props.initialvalues?.id || undefined,
        CerboTags: form.cerboTags.join(","),
        CRMTag: form.crmTag,
        Ordername: form.OrderName,
        CustomFieldId: form.customField || undefined,
        CustomFieldValue: form.customFieldValue || undefined,
        SelectedTagId: form.selectedTag || undefined,
        TagNotes: form.tagNotes || undefined,
      });
      
      if (response.success) {
        setSaving(false);
        handleCancel();
      } else {
        setSaving(false);
        setError(response.error || "Failed to save. Please try again.");
      }
    } catch (err) {
      setSaving(false);
      setError("Failed to save. Please try again.");
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#FAFAFC",
          boxShadow: "0 2px 16px rgba(24,26,42,0.10)",
          minWidth: 320,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Configure Open Order Rule</DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, sm: 3 },
          maxHeight: "80vh",
          overflowY: "auto",
          minHeight: 0,
          background: "#FAFAFC",
        }}
      >
        <Box sx={{ width: "100%" }}>
           <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">1</Box>
            <Box
              sx={{
                position: "absolute",
                left: 19,
                top: 38,
                bottom: 0,
                width: 2,
                bgcolor: "#e2e8f0",
              }}
            />

            
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  Order Name
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                    Enter the name for this open order rule.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                direction="column"
                sx={{ mt: 3, mb: 4, ml: 3 }}
              >
                <Grid>
                  <TextField
                    label="Order Name"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={form.OrderName}
                    onChange={(e) => {
                      handleChange("OrderName", e.target.value);
                      if (validationErrors.OrderName) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          OrderName: "",
                        }));
                      }
                    }}
                    error={!!validationErrors.OrderName}
                    helperText={validationErrors.OrderName}
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 2,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 14,
                        color: "#23244A",
                        borderRadius: 2,
                        "& fieldset": {
                          borderColor: "#dedede",
                        },
                        "&:hover fieldset": {
                          borderColor: "#3D52A0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3D52A0",
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
          {/* Step 1: Cerbo Tags */}
          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">2</Box>
            <Box
              sx={{
                position: "absolute",
                left: 19,
                top: 38,
                bottom: 0,
                width: 2,
                bgcolor: "#e2e8f0",
              }}
            />

            
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  Cerbo Tags 
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Select the Cerbo tags for this open order rule.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                direction="column"
                sx={{ mt: 3, mb: 4, ml: 3 }}
              >
                <Grid>
                  <Autocomplete
                    multiple
                    options={props.tagList}
                    getOptionLabel={(option) => option.name}
                    disableCloseOnSelect
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            minHeight: 32,
                          },
                        },
                      },
                    }}
                    value={props.tagList.filter((tag) => form.cerboTags.includes(tag.id))}
                    onChange={(_, newValue) => {
                      handleChange("cerboTags", newValue ? newValue.map((tag) => tag.id) : []);
                      if (validationErrors.cerboTags) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          cerboTags: "",
                        }));
                      }
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cerbo Tags"
                        size="small"
                        fullWidth
                        variant="outlined"
                        error={!!validationErrors.cerboTags}
                        helperText={validationErrors.cerboTags}
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#dedede",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3D52A0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3D52A0",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: 13,
                            color: "#23244A",
                          },
                        }}
                      />
                    )}
                    sx={{
                      width: "100%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Step 2: GHL Tag */}
          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">3</Box>
            <Box
              sx={{
                position: "absolute",
                left: 19,
                top: 38,
                bottom: 0,
                width: 2,
                bgcolor: "#e2e8f0",
              }}
            />
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  HL Tag 
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Select the HL tag to apply when this rule is triggered.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                direction="column"
                sx={{ mt: 3, mb: 4, ml: 3 }}
              >
                <Grid>
                  <Autocomplete
                    options={props.crmTags}
                    getOptionLabel={(option) => option.name}
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            minHeight: 32,
                          },
                        },
                      },
                    }}
                    value={props.crmTags?.find((tag) => tag.id === form.crmTag) || null}
                    onChange={(_, newValue) => {
                      handleChange("crmTag", newValue ? newValue.id : "");
                      if (validationErrors.crmTag) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          crmTag: "",
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="HL Tag"
                        size="small"
                        fullWidth
                        variant="outlined"
                        error={!!validationErrors.crmTag}
                        helperText={validationErrors.crmTag}
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#dedede",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3D52A0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3D52A0",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: 13,
                            color: "#23244A",
                          },
                        }}
                      />
                    )}
                    sx={{
                      width: "100%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Step 4: Custom Field */}
          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">4</Box>
            <Box
              sx={{
                position: "absolute",
                left: 19,
                top: 38,
                bottom: 0,
                width: 2,
                bgcolor: "#e2e8f0",
              }}
            />
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    Custom Field
                  </Typography>
                  <span style={{ fontWeight: 600, color: "#888" }}>(Optional)</span>
                </Box>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Choose an optional custom field and provide a value.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2, mt: 3, mb: 4, ml: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    select
                    label="Custom Field"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={form.customField}
                    onChange={(e) => {
                      handleChange("customField", e.target.value);
                    }}
                    SelectProps={{
                      native: false,
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            borderRadius: 2,
                            "& .MuiMenuItem-root": {
                              fontSize: 13,
                              minHeight: 32,
                              color: "#23244A",
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                      "& .MuiOutlinedInput-root": {
                        fontSize: 14,
                        color: "#23244A",
                        borderRadius: 2,
                        "& fieldset": {
                          borderColor: "#dedede",
                        },
                        "&:hover fieldset": {
                          borderColor: "#3D52A0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3D52A0",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                        color: "#23244A",
                      },
                    }}
                  >
                    {(props.customFieldList || []).map((field) => (
                      <MenuItem key={field.id} value={field.id}>
                        {field.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Custom Field Value"
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter a value"
                    value={form.customFieldValue}
                    onChange={(e) => {
                      handleChange("customFieldValue", e.target.value);
                    }}
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 2,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 14,
                        color: "#23244A",
                        borderRadius: 2,
                        "& fieldset": {
                          borderColor: "#dedede",
                        },
                        "&:hover fieldset": {
                          borderColor: "#3D52A0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3D52A0",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                        color: "#23244A",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Step 5: Tag Selection & Notes */}
          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">5</Box>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    Tag Selection & Notes
                  </Typography>
                  <span style={{ fontWeight: 600, color: "#888" }}>(Optional)</span>
                </Box>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Select a tag and add optional notes for reference.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                sx={{ mt: 3, mb: 4, ml: 3 }}
              >
                <Grid size ={6}>
                  <Autocomplete
                    options={props.tagList}
                    getOptionLabel={(option) => option.name}
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            minHeight: 32,
                          },
                        },
                      },
                    }}
                    value={props.tagList?.find((tag) => tag.id === form.selectedTag) || null}
                    onChange={(_, newValue) => {
                      handleChange("selectedTag", newValue ? newValue.id : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Tag"
                        size="small"
                        fullWidth
                        variant="outlined"
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#dedede",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3D52A0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3D52A0",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: 13,
                            color: "#23244A",
                          },
                        }}
                      />
                    )}
                    sx={{
                      width: "100%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                    }}
                  />
                </Grid>
                <Grid size ={6}>
                  <TextField
                    label="Tag Notes"
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={1}
                    placeholder="Add optional notes here"
                    value={form.tagNotes}
                    onChange={(e) => {
                      handleChange("tagNotes", e.target.value);
                    }}
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 2,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 14,
                        color: "#23244A",
                        borderRadius: 2,
                        "& fieldset": {
                          borderColor: "#dedede",
                        },
                        "&:hover fieldset": {
                          borderColor: "#3D52A0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3D52A0",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                        color: "#23244A",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 2, background: "#FAFAFC" }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            minWidth: 110,
            border: "none",
            background: "#dedede",
            color: "#000",
            textTransform: "capitalize",
            "&:hover": { background: "#d6d6d6" },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={saving || !isFormValid}
          onClick={handleSubmit}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            minWidth: 160,
            background: "#1eac53",
            textTransform: "capitalize",
            "&:hover": { background: "#1a9849" },
          }}
        >
          {saving ? "Saving..." : "Save & Continue"}
        </Button>
      </DialogActions>
      {error && (
        <Box
          sx={{
            color: "#d32f2f",
            textAlign: "center",
            pb: 2,
            px: 3,
            fontWeight: 500,
          }}
        >
          {error}
        </Box>
      )}
    </Dialog>
  );
};

export default AddOrders;
